import { AfterViewInit, Component, OnInit, Inject } from '@angular/core';
import { FooterComponent } from '../../layout/footer/footer.component';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import _ from 'lodash';
import { VendorInvoiceService } from '../../service/vendor-invoice.service';
import { UtilityService } from '../../service/utility.service';
import { NotificationService } from '../../service/notification.service';
import Swal from 'sweetalert2';
import { LoadingService } from '../../service/loading.service';
import { SessionService } from '../../service/session.service';
import { ConfigService } from '../../service/config.service';

// Shared by the form group and resetForm(); reset() nulls every control it is not given a value for,
// and a null id is rejected by the API as it binds to a non-nullable Guid.
const INVOICE_FORM_DEFAULTS = {
  id: '00000000-0000-0000-0000-000000000000',
  IsPo: true,
  OriginalPoAmount: 0.0,
  PoOpenAmount: 0.0,
};

@Component({
  selector: 'app-vendor-invoice',
  standalone: true,
  imports: [FormsModule, CommonModule, ReactiveFormsModule],
  templateUrl: './vendor-invoice.component.html',
  styleUrl: './vendor-invoice.component.css',
})
export class VendorInvoiceComponent implements OnInit, AfterViewInit {
  form: FormGroup;
  files: any[] = [];
  submitted = false;
  item: any;
  readonly id!: string;
  readonly clientId!: string;
  isDocumentDeleted = false;
  deletedDocumentIds = [];
  PoNumber: string;
  canSubmit = true;
  purchaseOrdersEnabled = true;

  get isLogedIn(): boolean {
    return this.sessionService.isAuthenticated();
  }

  constructor(
    private formBuilder: FormBuilder,
    @Inject(VendorInvoiceService)
    private vendorInvoiceService: VendorInvoiceService,
    public utilityService: UtilityService,
    private notificationService: NotificationService,
    private spinner: LoadingService,
    private sessionService: SessionService,
    private configService: ConfigService,
  ) {
    this.form = this.formBuilder.group({
      id: [INVOICE_FORM_DEFAULTS.id],
      IsPo: [INVOICE_FORM_DEFAULTS.IsPo],
      PoNumber: [null],
      OriginalPoAmount: [INVOICE_FORM_DEFAULTS.OriginalPoAmount, Validators.required],
      PoOpenAmount: [INVOICE_FORM_DEFAULTS.PoOpenAmount],
      ContactName: [null, Validators.required],
      ContactEmail: [null, [Validators.email, Validators.required]],
      InvoiceNumber: [null, Validators.required],
      InvoiceDescription: [
        null,
        [Validators.required, Validators.maxLength(50)],
      ],
      attachments: new FormArray([]),
      VendorName: [null],
      VendorId: [null],
      InvoiceDate: [null, Validators.required],
    });
  }
  setPoNumberValidator(status: boolean) {
    const PoNumberControl = this.form.get('PoNumber');
    if (status) {
      PoNumberControl?.setValidators([Validators.required]);
    } else {
      PoNumberControl?.clearValidators();
    }
    PoNumberControl?.updateValueAndValidity();
  }

  ngOnInit() {
    this.setPoNumberValidator(this.form.get('IsPo')!.value);
    this.form.get('IsPo')!.valueChanges.subscribe((status) => {
      this.setPoNumberValidator(status);
    });
    this.configService.whenResolved().subscribe((config) => {
      this.purchaseOrdersEnabled = this.configService.isFeatureEnabled(
        config.purchaseOrdersEnabled,
      );
      if (!this.purchaseOrdersEnabled) {
        this.form.get('IsPo')!.setValue(false);
      }
    });
    this.sessionService.canSubmitInvoice$().subscribe((canSubmit) => {
      this.canSubmit = canSubmit;
    });
  }

  ngAfterViewInit(): void {}

  get f() {
    return this.form.controls;
  }

  getInvoiceDescriptionCharCount(): number {
    const description = this.form.get('InvoiceDescription')?.value;
    return description ? description.length : 0;
  }

  isInvoiceDescriptionLimitExceeded(): boolean {
    return this.getInvoiceDescriptionCharCount() > 50;
  }

  onSubmit() {
    if (!this.sessionService.canSubmitInvoice()) {
      this.notificationService.showError(
        'Your session has ended. Please log in again to submit an invoice.',
        'Error',
      );
      this.sessionService.redirectToLogin('/vendor-invoice');
      return;
    }
    this.submitted = true;
    if (this.form.invalid) {
      this.utilityService.getFormValidationErrors(this.form);
      this.notificationService.showError('Fill all required fields', 'Error');
      return;
    }
    if (this.isLogedIn === true) {
      const registrationData = JSON.parse(
        localStorage.getItem('registrationData') || '{}',
      );
      if (registrationData?.name) {
        this.form.get('VendorName')?.setValue(registrationData.name);
        this.form.get('VendorId')?.setValue(registrationData.id);
      }
    }
    if (this.isLogedIn === true && this.form.get('IsPo')?.value === true) {
      this.PoNumber = this.form.get('PoNumber')?.value;
      const vendorId = JSON.parse(localStorage.getItem('vendorId') || 'null');

      this.spinner.OnLoading.emit(true);
      this.vendorInvoiceService
        .checkPoForVendor(this.PoNumber, vendorId)
        .subscribe(
          (data: any) => {
            this.spinner.OnLoading.emit(false);
            if (data) {
              this.createFormRequest();
            } else {
              Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'The PO number provided does not match the logged in vendor purchase orders available in the system. Please review PO and retry.',
              });
            }
          },
          (err: any) => {
            this.spinner.OnLoading.emit(false);
            Swal.fire({
              icon: 'error',
              title: 'Validation Error',
              text: 'An error occurred while validating the PO.',
            });
            this.notificationService.showError(err, 'Error!');
          },
        );

      return;
    }

    this.createFormRequest();
  }

  private createFormRequest() {
    const request = _.cloneDeep(this.form.value);
    const requestArray = [request];

    this.spinner.OnLoading.emit(true);
    this.vendorInvoiceService.createForm(requestArray).subscribe(
      (data: any) => {
        this.spinner.OnLoading.emit(false);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Invoice saved successfully!',
        }).then(() => {
          window.location.reload();
        });
      },
      (err: any) => {
        this.spinner.OnLoading.emit(false);
        const validationMessage =
          err?.status === 400 && typeof err.error === 'string' && err.error.trim()
            ? err.error
            : null;
        Swal.fire({
          icon: 'error',
          title: 'Submission Error',
          text:
            validationMessage ??
            'An error occurred while submitting the form. Please try again later.',
        });
        this.notificationService.showError(err, 'Error!');
      },
    );
  }

  onAdditionalFileChanged(file: any, i: number) {
    const currentVal = this.filesArray.controls[i].value;
    currentVal.file = file;
    this.filesArray.controls[i].patchValue(currentVal);
  }

  onFileChange($event: any, propertyName: string) {
    this.files.push($event);
    this.form.get(propertyName)?.patchValue($event);
  }

  async onFileSelected(event: any, fileInput: HTMLInputElement): Promise<void> {
    const overallMaxSize = 45 * 1024 * 1024; // 30MB in bytes
    const selectedFiles = event.target.files;

    if (!selectedFiles) return;

    // Calculate the current total size of selected files
    let currentTotalSize = this.filesArray.controls.reduce(
      (sum, control) => sum + control.value.file.size,
      0,
    );

    for (const file of selectedFiles) {
      if (this.utilityService.isFileEmptyOrCorrupted(file)) {
        this.notificationService.showError(
          this.utilityService.getCorruptedOrEmptyFileMessage(),
          'Invalid File',
        );
        continue;
      }

      if (!this.utilityService.isFileTypeAllowed(file)) {
        this.notificationService.showError(
          this.utilityService.getUnsupportedFileTypeMessage(file.name),
          'Invalid File Type',
        );
        continue;
      }

      if (file.size > overallMaxSize) {
        this.notificationService.showError(
          `"${file.name}" is over the individual file size limit. Please upload a file under 45MB.`,
          'Error',
        );
        continue;
      }

      // Check if adding this file exceeds the total allowed size
      if (currentTotalSize + file.size > overallMaxSize) {
        const remainingSizeMB = (
          (overallMaxSize - currentTotalSize) /
          (1024 * 1024)
        ).toFixed(2);
        this.notificationService.showError(
          `"${file.name}" cannot be added. You can only upload ${remainingSizeMB}MB more.`,
          'Error',
        );
        continue;
      }

      const restrictedZipMessage =
        await this.utilityService.getRestrictedZipContentMessage(file);
      if (restrictedZipMessage) {
        this.notificationService.showError(
          restrictedZipMessage,
          'Invalid File Type',
        );
        continue;
      }

      // Update total size and add the file
      currentTotalSize += file.size;
      const currentFileCount = this.filesArray.controls.length + 1;
      this.filesArray.push(
        new FormControl({
          label: `File ${currentFileCount}`,
          file,
          order: currentFileCount,
        }),
      );
    }

    fileInput.value = ''; // Reset file input
  }

  removeFile(index: number) {
    if (this.filesArray.controls.length > 0) {
      this.filesArray.removeAt(index);
    }
  }

  addFile() {
    const currentFileCount = this.filesArray.controls.length + 1;
    this.filesArray.push(
      new FormControl({
        label: `Additional File ${currentFileCount}`,
        file: null,
        order: currentFileCount,
      }),
    );
  }

  resetForm() {
    this.submitted = false;
    while (this.filesArray.length) {
      this.filesArray.removeAt(0);
    }
    this.form.reset(INVOICE_FORM_DEFAULTS);
  }

  get filesArray(): FormArray {
    return this.form.controls['attachments'] as FormArray;
  }

  onIsPoChanged() {
    this.form.get('PoNumber')?.setValue('');
  }
}
